import { Grid, Menu, MenuItem, Theme, Typography, useTheme } from '@mui/joy';
import { Check } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from 'core/elements';
import { PricingHelper } from 'pricing/helpers';
import { QuotePartPriceGuidanceSourceType } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { useGetQuotePartPricingGuidance, useQuotePartActions } from 'quotes/hooks';
import { IQuotePart, IQuotePartPricingGuidance } from 'quotes/types';
import { Color, Level, Size } from 'theme/enums';

interface IProps {
  quotePart: IQuotePart;
  open: boolean;
  onPriceUpdateSuccess: (updatedLine: IQuotePart) => void;
}

export const PricingGuidanceMenu = ({ quotePart, open, onPriceUpdateSuccess }: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { records, isLoading, isFetching } = useGetQuotePartPricingGuidance(quotePart.id, open);

  const { setPriceBySource } = useQuotePartActions();

  const onChangePrice = async (sourceType: QuotePartPriceGuidanceSourceType) => {
    const { success, quotePart: updatedQuotePart } = await setPriceBySource({
      quotePartId: quotePart.id,
      source: sourceType,
    });

    if (!success) {
      return;
    }

    onPriceUpdateSuccess(updatedQuotePart);
  };

  const priceSourceType = QuotePartHelper.convertPriceSourceToPricingGuidanceSourceType(
    quotePart.priceSourceType
  );

  const isLoadingRecords = useMemo(() => {
    return isLoading || isFetching;
  }, [isLoading, isFetching]);

  return (
    <Menu placement="bottom-end" sx={{ minWidth: 290 }}>
      <Typography level={Level.TITLE_LG} sx={titleMenuStyle(theme)}>
        {t('labelPricingGuidance')}
      </Typography>

      {isLoadingRecords && (
        <MenuItem sx={noPriceMenuItemStyle}>
          <Loader size={Size.SM} />
        </MenuItem>
      )}

      {!isLoadingRecords && records.length === 0 && (
        <MenuItem sx={noPriceMenuItemStyle}>{t('labelNoPriceAvailable')}</MenuItem>
      )}

      {!isLoadingRecords &&
        records.map((priceGuidance: IQuotePartPricingGuidance) => {
          const isSelected = priceSourceType === priceGuidance.sourceType;

          const priceLabel = QuotePartHelper.pricingLabel({
            source: priceGuidance.sourceType,
            isAlternateVersionEnabled: Boolean(quotePart.alternateVersionEnabled),
            hasColon: false,
          });

          const priceValue = PricingHelper.formatPricing({
            amount: priceGuidance.price,
            decimals: quotePart.quotePriceDecimals,
          });

          return (
            <MenuItem
              key={priceGuidance.sourceType}
              sx={menuItemStyle}
              onClick={() => onChangePrice(priceGuidance.sourceType)}
            >
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <Grid
                  xs={1.5}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {isSelected && (
                    <Typography color={Color.SUCCESS}>
                      <Check size={16} />
                    </Typography>
                  )}
                </Grid>
                <Grid
                  xs={10.5}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-right' }}
                >
                  <Typography level={Level.BODY_SM} color={isSelected ? undefined : Color.NEUTRAL}>
                    {`${priceLabel} (${priceValue})`}
                  </Typography>
                </Grid>
              </Grid>
            </MenuItem>
          );
        })}
    </Menu>
  );
};

export const titleMenuStyle = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 56,
  borderBottom: `1px solid ${theme.palette.neutral[200]}`,
  fontWeight: 500,
});

export const menuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  height: 48,
  width: '100%',
};

export const noPriceMenuItemStyle = {
  ...menuItemStyle,
  justifyContent: 'center',
};
